import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import HeadingDots from './HeadingDots';

const slides = [
  { src: '/spiick_imgs/reviews/r1.png', name: 'Sarah', meta: 'Poitiers, France · 10 ans' },
  { src: '/spiick_imgs/reviews/r2.png' },
  { src: '/spiick_imgs/reviews/r3.png' },
  { src: '/spiick_imgs/reviews/r4.png' }
];

export default function Testimonials() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (index) => {
    const el = trackRef.current;
    if (!el) return;
    const items = el.querySelectorAll('[data-slide]');
    const target = items[Math.max(0, Math.min(slides.length - 1, index))];
    if (target) {
      const left = target.offsetLeft - el.offsetLeft - (el.clientWidth - target.clientWidth) / 2 + 0; // center the active slide
      el.scrollTo({ left, behavior: 'smooth' });
    }
  };

  const onPrev = () => scrollTo(active - 1);
  const onNext = () => scrollTo(active + 1);

  // Track which slide is most centered
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const items = el.querySelectorAll('[data-slide]');
        const center = el.scrollLeft + el.clientWidth / 2;
        let bestIdx = 0;
        let bestDist = Infinity;
        items.forEach((node, i) => {
          const c = node.offsetLeft + node.clientWidth / 2 - el.offsetLeft;
          const d = Math.abs(c - center);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        });
        setActive(bestIdx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section className="relative w-full bg-white py-10 lg:py-14">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="text-center">
          <h2
            className="text-[48px] sm:text-[56px] lg:text-[64px] leading-[1.04] text-[#0c0a1a] tracking-tight"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Loved by beta testers<br />all over{' '}
            <span className="relative inline-block align-baseline whitespace-nowrap">
              <svg
                aria-hidden="true"
                viewBox="0 0 220 80"
                preserveAspectRatio="none"
                className="absolute -left-[4%] -right-[4%] top-[14%] -bottom-[10%] w-[108%] h-[96%] z-0 pointer-events-none"
              >
                <path
                  d="M9 32 C 30 22, 70 18, 112 16 C 150 14, 190 18, 213 26 C 218 38, 214 54, 208 64 C 175 72, 130 71, 92 70 C 60 70, 24 68, 7 60 C 2 50, 4 38, 9 32 Z"
                  fill="#abf1b2"
                  opacity="0.72"
                />
              </svg>
              <span className="relative z-10" style={{ color: '#004867' }}>the world</span>
            </span>
          </h2>

          <div className="hidden md:flex items-center justify-center gap-3 mt-8">
            <button
              onClick={onPrev}
              aria-label="Previous"
              disabled={active === 0}
              className="w-12 h-12 rounded-full border border-[#0c0a1a]/15 grid place-items-center text-[#0c0a1a] hover:bg-[#0c0a1a] hover:text-white hover:border-[#0c0a1a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={onNext}
              aria-label="Next"
              disabled={active === slides.length - 1}
              className="w-12 h-12 rounded-full border border-[#0c0a1a]/15 grid place-items-center text-[#0c0a1a] hover:bg-[#0c0a1a] hover:text-white hover:border-[#0c0a1a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Carousel — full bleed for peek effect */}
      <div className="relative mt-12 lg:mt-14">
        <div
          ref={trackRef}
          className="flex gap-5 lg:gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-6 lg:px-[max(2.5rem,calc((100vw-1100px)/2))] pb-2"
          style={{ scrollbarWidth: 'none' }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.src}
              data-slide
              className="snap-center shrink-0 w-[78vw] sm:w-[55vw] md:w-[42vw] lg:w-[36vw] xl:w-[420px]"
            >
              <div className="aspect-square rounded-[24px] overflow-hidden">
                <img
                  src={slide.src}
                  alt=""
                  draggable={false}
                  className="w-full h-full object-cover select-none"
                />
              </div>
              {slide.name && (
                <div className="mt-4 text-center">
                  <h3
                    className="text-[36px] lg:text-[40px] leading-tight tracking-tight"
                    style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' }}
                  >
                    <span className="relative inline-block align-baseline whitespace-nowrap">
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 220 80"
                        preserveAspectRatio="none"
                        className="absolute -left-[6%] -right-[6%] top-[14%] -bottom-[10%] w-[112%] h-[96%] z-0 pointer-events-none"
                      >
                        <path
                          d="M9 32 C 30 22, 70 18, 112 16 C 150 14, 190 18, 213 26 C 218 38, 214 54, 208 64 C 175 72, 130 71, 92 70 C 60 70, 24 68, 7 60 C 2 50, 4 38, 9 32 Z"
                          fill="#f8d768"
                          opacity="0.72"
                        />
                      </svg>
                      <span className="relative z-10" style={{ color: '#0c0a1a' }}>{slide.name}</span>
                    </span>
                  </h3>
                  {slide.meta && (
                    <p
                      className="mt-3 text-[14px] lg:text-[15px] text-[#555266]"
                      style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                    >
                      {slide.meta}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Dots — mobile */}
        <div className="md:hidden mt-6 flex items-center justify-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all ${i === active ? 'w-6 bg-[#0c0a1a]' : 'w-2 bg-[#0c0a1a]/25'}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}
